import React from "react";
import PropTypes from "prop-types";
import { typography, textColors } from "../../tokens";

/**
 * StatusBadge
 *
 * A small pill-shaped badge for status indicators (Published/Draft, Success/Warning/Error, etc).
 *
 * Key rule: if the background is intentionally *light* even in dark mode (e.g. bg-yellow-100),
 * the text must remain dark to preserve contrast. Use `textColors.onLight` for those cases.
 */
const StatusBadge = ({ variant = "neutral", size = "md", children, className = "", ...rest }) => {
  const sizeClasses =
    size === "sm"
      ? ["px-2", "py-0.5", typography.caption]
      : ["px-3", "py-1", typography.label];

  const baseClasses = ["inline-flex", "items-center", "rounded-full", ...sizeClasses];

  const variantClasses = {
    success: "bg-green-100 text-green-800 dark:bg-green-100 dark:text-green-900 border border-green-200 dark:border-green-200",
    // Match the common Tailwind default chip style.
    // We keep the pill background light in both themes, so the text remains a dark amber in both themes.
    warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-100 dark:text-yellow-800 border border-yellow-200 dark:border-yellow-200",
    danger: "bg-red-100 text-red-800 dark:bg-red-100 dark:text-red-900 border border-red-200 dark:border-red-200",
    neutral: "bg-gray-100 text-gray-800 dark:bg-gray-100 dark:text-gray-900 border border-gray-200 dark:border-gray-200",
    info: "bg-blue-100 text-blue-800 dark:bg-blue-100 dark:text-blue-900 border border-blue-200 dark:border-blue-200",
  };

  const classes = [...baseClasses, variantClasses[variant] || variantClasses.neutral, className].join(" ");

  return (
    <span className={classes} {...rest}>
      {children}
    </span>
  );
};

StatusBadge.propTypes = {
  /** Visual style */
  variant: PropTypes.oneOf(["success", "warning", "danger", "neutral", "info"]),
  /** Size */
  size: PropTypes.oneOf(["sm", "md"]),
  /** Badge content */
  children: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default StatusBadge;

